import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 8.1 visual preview at /preview/lesson/8.1
 * "What actually changed and what didn't"
 * Sorting real AI capability unlocks from noise.
 */

export default function LessonPreviewPage8_1() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromPrev />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 8 · AI in a world where it has impacted everything · Lesson 8.1
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        What actually changed and what didn't
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Noise filter · Capability · Posture</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 unlock-sorting drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="Your feed says everything changed this week. Your actual work has not changed in three months. Both are true.">
      <p className="mt-6 text-text/90">
        Open any tech feed today. You will see twelve headlines about AI before
        you finish your coffee. New models. New benchmarks. New companies.
        New “this changes everything” takes.
      </p>
      <p className="mt-4 text-text/90">
        Now look at your actual work this week. The brief you owe Friday. The
        deck for the team meeting. The code review you put off twice. How many
        of those twelve headlines changed how you would do any of it?
      </p>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The gap
        </p>
        <p className="mt-3 text-text/90">
          The AI news cycle moves faster than the AI capability curve. New
          announcements arrive weekly. Real unlocks in what you can actually do
          with the tools arrive every few months. The headlines and the unlocks
          are not the same thing, and treating them as the same thing burns
          your attention on things that do not matter.
        </p>
      </div>

      <p className="mt-6 text-text/90">
        Week 8 starts here because you cannot operate well in a world where AI
        has impacted everything if you cannot tell which changes are real. The
        first move is to sort signal from noise.
      </p>
      <p className="mt-4 text-text/90">
        Lesson 8.1 is about the difference between a capability unlock and a headline. An
        unlock changes the work. A headline changes the feed. You will need
        both filters: the one that catches real unlocks early enough to use
        them, and the one that lets you ignore the rest without guilt.
      </p>
    </Section>
  );
}

/* --- Section 2: From 7.5 --- */
function FromPrev() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Week 7 capstone" title="Week 7 gave you a standard with other people. Week 8 gives you a standard with the field.">
      <p className="mt-6 text-text/90">
        At the end of Week 7 your team had a one-page AI standard. Five
        sections, written together, revisited every six weeks. That standard
        only holds if you can keep up with what is actually changing in the
        tools. Otherwise the doc goes stale and the team drifts back to
        whatever the loudest headline said.
      </p>
      <p className="mt-4 text-text/90">
        Week 8 is the close. Not new techniques. Not new vocab piled on. A
        small set of habits that hold up while the field keeps moving. By the
        end of this week you have your own operator brief: how you work, what
        tools you use, how you stay sharp, and how you catch real changes
        when they arrive.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="One sentence on what the Week 7 capstone shipped." />
        <ReviewItem n="02" q="Why a team standard goes stale without a noise filter." />
        <ReviewItem n="03" q="The Week 8 posture, in one phrase." />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The unlock filter" title="Most AI news is noise. A small percent is a capability unlock that changes your work. Here is how to tell them apart.">
      <Subhead n={1} title="The vocab for the lesson" />
      <VocabCard
        term="Capability unlock"
        definition="A real change in what AI can do that actually changes how you can do your work, not just a headline. An unlock opens up a job you could not do before, or makes a job you do regularly meaningfully cheaper, faster, or better. Most news is not an unlock."
        analogy="Think about your phone. A new iOS theme is a headline. A new camera sensor that lets you photograph things you genuinely could not photograph before is an unlock. You spent twenty years not caring about most updates, and a handful of times an update actually changed what your phone was for. AI moves the same way, just faster."
        memoryHook="An unlock changes the work. A headline changes the feed. If the news does not change a job you actually do, it is not your unlock yet."
        why="Your attention is finite. The AI field generates more news than any human can track and stay sane. An unlock filter lets you ignore most of it without guilt, and stay sharp on the small percent that actually matters. Operators who cannot filter end up chasing every announcement and shipping nothing. Operators who can filter look calm while everyone else is panicking, and they catch the real changes a beat early. &lsquo;Capability unlock&rsquo; is the same word AI labs and researchers use when a new model can do something the last one could not."
      />

      <Subhead n={2} title="The four tests of a real unlock" className="mt-12" />
      <p className="mt-4 text-text/90">
        Any news item only counts as an unlock if it passes all four. Three out
        of four is interesting. Four out of four is something to actually
        change your habits around.
      </p>
      <TestsGrid />

      <Subhead n={3} title="The three flavors of noise" className="mt-12" />
      <p className="mt-4 text-text/90">
        Most AI news is one of three things, none of which is an unlock. Naming
        the flavor makes it easier to scroll past without anxiety.
      </p>
      <NoiseGrid />

      <Subhead n={4} title="Where AI helps and where AI hurts on filtering" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is useful for processing the volume of news. It is bad at deciding
        what matters to your specific work. Use it for the first pass. Do not
        let it do the deciding.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The weekly review, not the daily scroll" className="mt-12" />
      <p className="mt-4 text-text/90">
        You do not need to track AI news every day. You need to do one focused
        review per week. Block thirty minutes. Look at what you did in the
        last seven days. Ask: did any announcement in this window change a
        job I do regularly? Most weeks the answer is no. When the answer
        is yes, that is your unlock. Run it through the four tests before you
        change anything.
      </p>
      <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          The rule
        </p>
        <p className="mt-3 text-text/90">
          Daily scrolling makes you anxious. A weekly review keeps you current
          without whiplash. Seven days is enough time for a launch to settle
          and short enough that you will not miss a real unlock.
        </p>
      </div>
    </Section>
  );
}

const TESTS = [
  {
    n: '01',
    name: 'Capability test',
    body: 'Does this actually unlock a job you could not do before, or make a regular job meaningfully cheaper, faster, or better? “Slightly higher benchmark score” does not count. “Now I can do X in 10 minutes that used to take an afternoon” counts.',
  },
  {
    n: '02',
    name: 'Access test',
    body: 'Can you actually use this today, in the tools you already pay for or could realistically reach? A research demo behind a paywall, a waitlist, or a paper is not your unlock yet. Wait for it to land in a real product or a real API before you change habits.',
  },
  {
    n: '03',
    name: 'Workload test',
    body: 'Does this touch a job you actually do, often? A new code-generation breakthrough is not your unlock if you do not write code. A new image model is not your unlock if you do not work in visuals. Most real unlocks only matter to a slice of people. That is fine.',
  },
  {
    n: '04',
    name: 'Stability test',
    body: 'Has the new capability held up across at least a couple of weeks of independent use? First-week demos look amazing. By week three the rough edges show. Wait long enough to read the second wave of takes from people who actually used it on real work.',
  },
];

function TestsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {TESTS.map((p) => (
        <div key={p.n} className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">Test {p.n}</span>
            <h4 className="text-lg font-semibold text-amber" dangerouslySetInnerHTML={{ __html: p.name }} />
          </div>
          <p
            className="mt-3 text-text/90"
            dangerouslySetInnerHTML={{ __html: p.body }}
          />
        </div>
      ))}
    </div>
  );
}

const NOISE = [
  {
    n: '01',
    name: 'Benchmark theater',
    body: 'A new model scores a few points higher on a benchmark. The benchmark is often gamed, often saturated, and rarely tracks anything you would notice doing real work. Scroll past. If the model is meaningfully different on your work, you will know without the leaderboard.',
  },
  {
    n: '02',
    name: 'Personality news',
    body: 'A founder said something on a podcast. Two labs are fighting. Someone left a company. Real people, real drama, zero impact on what you can ship this week. Treat it like sports news: fine to enjoy, not a reason to change your stack.',
  },
  {
    n: '03',
    name: 'Demo bait',
    body: 'A polished sixty-second video of a model doing something extraordinary. No public release date, no way to verify, no real-work footage. The demo is real. The reliability behind it is unknown. File the headline. Do not change anything until you can put hands on it.',
  },
];

function NoiseGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-3">
      {NOISE.map((p) => (
        <div key={p.n} className="rounded-lg border border-rose-500/40 bg-rose-500/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-mono text-xs text-muted">Noise {p.n}</span>
          </div>
          <h4 className="mt-2 text-lg font-semibold text-rose-300">{p.name}</h4>
          <p className="mt-3 text-sm text-text/90">{p.body}</p>
        </div>
      ))}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Summarizing a week of AI news into a short digest you can scan in five minutes.',
      'Pulling the actual claims out of a hype-heavy announcement so you can see what is being promised.',
      'Comparing a new tool against the one you already use, on a specific job you actually do.',
      'Drafting questions you would want answered before you change any habit.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'Deciding what matters: AI does not know your work. It will treat every announcement as significant.',
      'Setting your update cadence: if you ask AI hourly, it will summarize hourly. The cadence is yours to set.',
      'Replacing the four tests: a chatbot saying “this is a major release” is not a substitute for the workload and access tests.',
      'Calming your anxiety: scrolling a news summary feels productive. Doing your actual work is the actual fix.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span dangerouslySetInnerHTML={{ __html: it }} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Unlock or noise" title="Three headlines from a real news cycle. Run each through the filter and decide.">
      <p className="mt-6 text-text/90">
        For each headline, two reads could plausibly apply. One is the better
        call given the four tests. Pick the read that lines up with the
        evidence, not the one that sounds the most exciting.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <ShiftDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  scene: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    scene:
      'A new chat model is announced. It scores 2 points higher on a popular reasoning benchmark than the model you use today. Available next month for current subscribers at the same price. You write briefs and project plans, mostly.',
    optionA:
      'Real unlock: a higher reasoning score means better briefs and plans.',
    optionB:
      'Noise: benchmark theater, no clear change to the jobs you actually do.',
    correct: 'B',
    verdict:
      'A 2-point benchmark bump rarely shows up in everyday writing tasks. It passes the access test (same price, near release) but fails the capability test (does this unlock a job you could not do) and the workload test (will you actually notice on briefs and plans). The right move is to try it side by side on one real brief when it ships, and only then decide whether to switch your default. Until then, scroll past without guilt.',
  },
  {
    n: '02',
    scene:
      'A coding assistant ships a feature that lets it work on a multi-file change in your editor, run the tests, and fix what breaks, all without you copy-pasting between windows. Available today in the tool you already use. You are learning to code as part of a class project.',
    optionA: 'Noise: another coding-assistant feature, fine to ignore.',
    optionB:
      'Real unlock: changes how you do a job you do regularly, available today, in a tool you already use.',
    correct: 'B',
    verdict:
      'This one passes all four tests. Capability: agentic multi-file editing changes the shape of how you work on code. Access: in your existing tool, available now. Workload: you code as part of a class. Stability: if it has been live for a few weeks, the early bugs are showing. The right move is to spend an hour with it on a real assignment, then decide whether to fold it into your default rhythm. If you ignore something like this, you fall behind quickly.',
  },
  {
    n: '03',
    scene:
      'A research lab posts a sixty-second video of a model generating a fully-edited short film from one paragraph of text. The video looks remarkable. There is no release date, no API, no public access. Comments are full of “this changes everything.”',
    optionA: 'Noise: demo bait, no access, nothing you can use this week.',
    optionB:
      'Real unlock: video generation just leveled up, time to retool your visual workflow.',
    correct: 'A',
    verdict:
      'The demo might be real. The product is not, and demos are often best-case reels. The headline fails the access test, almost certainly fails the stability test, and you cannot run the workload test on a tool you cannot touch. File it. Set a calendar reminder for three months. If the public version actually ships and holds up, that is when you re-evaluate. Until then, no change to your stack.',
  },
];

function ShiftDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The headline
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: drill.scene }}
      />

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Unlock or noise?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span
                  className="mt-2 block"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That read is tempting, but the four tests tell a different
              story. Run capability, access, workload, and stability against
              the evidence in the scene.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The call the four tests support' : 'Correct'} · Option {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90" dangerouslySetInnerHTML={{ __html: drill.verdict }} />
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Capability unlock in one sentence." answer="A real change in what AI can do that actually changes how you can do your work, not just a headline." />
        <QuickCheckItem n="Q2" q="The four tests of a real unlock." answer="Capability (opens up or improves a real job), access (you can use it today), workload (touches a job you actually do, often), stability (held up across a couple of weeks of independent use)." />
        <QuickCheckItem n="Q3" q="The three flavors of noise." answer="Benchmark theater, personality news, demo bait." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on filtering. Two things AI hurts." answer="Helps: summarizing a week of news, pulling claims out of hype, comparing tools on a specific job, drafting questions. Hurts: deciding what matters to your work, setting your cadence, replacing the four tests, calming your anxiety." />
        <QuickCheckItem n="Q5" q="Daily scroll or weekly review?" answer="Weekly review, thirty minutes, against the work you actually did. Daily scrolling makes you anxious. Weekly review keeps you current without whiplash." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The unlock filter.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 8.1 · Lock card · The unlock filter
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          A capability unlock changes the work. A headline changes the feed. Operators can
          tell the difference and protect their attention either way.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-2">
          <LockTile n="01" name="Capability" body="Does this unlock or improve a real job?" />
          <LockTile n="02" name="Access" body="Can you use it today, in your real tools?" />
          <LockTile n="03" name="Workload" body="Does it touch a job you actually do, often?" />
          <LockTile n="04" name="Stability" body="Held up across a couple of weeks of real use?" />
        </div>

        <div className="mt-6 rounded-md border border-rose-500/40 bg-rose-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
            The three flavors of noise
          </p>
          <p className="mt-2 text-text/90">
            Benchmark theater. Personality news. Demo bait. None of them
            change a job you do.
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The cadence
          </p>
          <p className="mt-2 text-text/90">
            Weekly review, thirty minutes, against the work you actually
            did. Most weeks the answer is “no unlock.” That is the
            point.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Your attention is finite. Spend it on the work, not the feed.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p
        className="mt-3 text-sm text-text/85"
        dangerouslySetInnerHTML={{ __html: body }}
      />
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 8.2
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The parts of you that don\'t get automated
          </h3>
          <p className="mt-3 text-sm text-text/85">
            What stays yours when the tools keep changing. Vocab: Anchor.
            The three pieces of your work AI cannot do for you, and how to
            invest in them on purpose.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 8.1 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From Week 7',
    'The unlock filter',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
